import { useEffect, useMemo, useState } from 'react';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import useInput from '../useInput';
import { useCreateProjectIndicationStore } from 'store/local/projectCreateindication';
import { UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { IImplantLibrary, ImplantLibraryData } from 'lib/types/clinic/projectTypes';
import { getJawByToothNumber } from 'lib/clinic/library';

export const useCreateProjectLibary = () => {
  // SECTION: declare server state
  const {
    loadedSource,
  }: {
    loadedSource: 'server' | 'local';
  } = useShallowAppSelector(state => ({
    loadedSource: state.app.loadedSource,
  }));

  const { setIsLibraryOpen } = useCreateProjectInfoStore();
  const { implantLibraryData, setImplantLibrary, tooth, setTooth, setTeeth } =
    useCreateProjectIndicationStore();
  const implantLibraryIdx = useInput(null);
  const companyKeyword = useInput(null);
  const implantKeyword = useInput(null);
  const typeKeyword = useInput(null);
  const subTypeKeyword = useInput(null);
  const isValidLibrary = useMemo(() => {
    return !!implantLibraryIdx.value;
  }, [implantLibraryIdx.value]);
  const [tabIndex, setTabIndex] = useState(0);

  const projectLibrary = useMemo(() => {
    if (!!implantLibraryData) {
      return implantLibraryData;
    }
  }, [implantLibraryData]);

  const companyList = useMemo(() => {
    return projectLibrary?.map(libraryItem => {
      return {
        index: libraryItem.companyKeyword,
        name: libraryItem.companyName,
        implantList: libraryItem.librayList,
      };
    });
  }, [projectLibrary]);

  const implantList = useMemo(() => {
    return companyList
      ?.find(companyItem => companyItem.index === companyKeyword.value)
      ?.implantList?.map(implantItem => {
        if ('implantLibraryIdx' in implantItem) {
          return {
            index: implantItem.keyword,
            name: implantItem.displayName,
            typeList: implantItem.typeList,
            implantLibraryIdx: implantItem.implantLibraryIdx,
          };
        } else {
          return {
            index: implantItem.keyword,
            name: implantItem.displayName,
            typeList: implantItem.typeList,
          };
        }
      });
  }, [companyList, companyKeyword]);

  const typeList = useMemo(() => {
    return implantList
      ?.find(implantItem => implantItem.index === implantKeyword.value)
      ?.typeList?.map(typeItem => {
        if ('implantLibraryIdx' in typeItem) {
          return {
            index: typeItem.keyword,
            name: typeItem.displayName,
            subTypeList: typeItem.subtypeList,
            implantLibraryIdx: typeItem.implantLibraryIdx,
          };
        } else {
          return {
            index: typeItem.keyword,
            name: typeItem.displayName,
            subTypeList: typeItem.subtypeList,
          };
        }
      });
  }, [implantList, implantKeyword.value]);

  const subTypeList = useMemo(() => {
    return typeList
      ?.find(typeItem => typeItem.index === typeKeyword.value)
      ?.subTypeList?.map(subTypeItem => {
        return {
          index: subTypeItem.keyword,
          name: subTypeItem.displayName,
          implantLibraryIdx: subTypeItem.implantLibraryIdx,
        };
      });
  }, [typeList, typeKeyword.value]);

  const handleSaveLibrary = (isApplyAll: boolean) => {
    const newImplantLibrary: IImplantLibrary = {
      implantLibraryIdx: implantLibraryIdx.value,
      companyName: companyList?.find(companyItem => companyItem.index === companyKeyword.value)
        ?.name,
      libraryDisplayName: implantList?.find(
        implantItem => implantItem.index === implantKeyword.value,
      )?.name,
      typeDisplayName: typeList?.find(typeItem => typeItem.index === typeKeyword.value)?.name,
      subtypeDisplayName: subTypeList?.find(
        subTypeItem => subTypeItem.index === subTypeKeyword.value,
      )?.name,
    };

    setTooth(prev => {
      return { ...prev, implantLibrary: newImplantLibrary };
    });

    if (isApplyAll) {
      setTeeth(prev =>
        prev.map(item => {
          if (item.pimType === 'normal') {
            return {
              ...item,
              implantLibrary: newImplantLibrary,
            };
          } else {
            return item;
          }
        }),
      );
    } else {
      setTeeth(prev =>
        prev.map(item => {
          if (item.number === tooth?.number) {
            return {
              ...item,
              implantLibrary: newImplantLibrary,
            };
          } else {
            return item;
          }
        }),
      );
    }
    setImplantLibrary(newImplantLibrary);
    setIsLibraryOpen(false);
  };

  const handleLibraryClose = () => {
    setIsLibraryOpen(false);
  };

  const handleTabClick = (index: number) => {
    setTabIndex(index);
  };

  const handleLibraryClick = (tabIndex: number, index: string, libraryIdx?: number) => {
    if (tabIndex === 0) {
      companyKeyword.setValue(index);
      implantKeyword.setValue(null);
      typeKeyword.setValue(null);
      subTypeKeyword.setValue(null);
    }
    if (tabIndex === 1) {
      implantKeyword.setValue(index);
      typeKeyword.setValue(null);
      subTypeKeyword.setValue(null);
      if (!!implantLibraryIdx) {
        implantLibraryIdx.setValue(libraryIdx);
      }
    }
    if (tabIndex === 2) {
      typeKeyword.setValue(index);
      subTypeKeyword.setValue(null);
      if (!!implantLibraryIdx) {
        implantLibraryIdx.setValue(libraryIdx);
      }
    }
    if (tabIndex === 3) {
      subTypeKeyword.setValue(index);
      if (!!implantLibraryIdx) {
        implantLibraryIdx.setValue(libraryIdx);
      }
    }
  };

  const handleDeleteItemClick = () => {
    companyKeyword.setValue(null);
    implantKeyword.setValue(null);
    typeKeyword.setValue(null);
    subTypeKeyword.setValue(null);
    implantLibraryIdx.setValue(null);
    setTabIndex(0);
  };

  useEffect(() => {
    return () => {
      handleDeleteItemClick();
    };
  }, []);

  useEffect(() => {
    if (!!companyKeyword.value && !!implantList && implantList.length > 0) {
      setTabIndex(1);
    }
  }, [companyKeyword.value, implantList]);

  useEffect(() => {
    if (!!implantKeyword.value && !!typeList && typeList.length > 0) {
      setTabIndex(2);
    }
  }, [implantKeyword.value, typeList]);

  useEffect(() => {
    if (!!typeKeyword.value && !!subTypeList && subTypeList.length > 0) {
      setTabIndex(3);
    }
  }, [typeKeyword.value, subTypeList]);

  return {
    tabIndex,
    isValidLibrary,
    companyKeyword,
    implantKeyword,
    typeKeyword,
    subTypeKeyword,
    companyList,
    implantList,
    typeList,
    subTypeList,
    onSaveLibrary: handleSaveLibrary,
    onLibraryClose: handleLibraryClose,
    onTabClick: handleTabClick,
    onLibraryClick: handleLibraryClick,
    onDeleteItemClick: handleDeleteItemClick,
  };
};
